import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, ExternalLink, Scale, Lock, AlertTriangle } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const LAST_UPDATED = 'April 20, 2026';

export default function DisclosuresPage() {
  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="disclosures-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/dashboard"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 animate-fade-in-up">
          <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-2">
            Legal
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold text-[#36454F]">
            Mandatory Disclosures
          </h1>
          <p className="mt-2 text-sm text-[#36454F]/60 font-['Manrope']">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <article className="bg-white border border-[#36454F]/10 rounded-sm p-6 sm:p-10 space-y-8 font-['Manrope'] text-[#36454F]/85 leading-relaxed text-sm">

          {/* What we are not */}
          <Section icon={<Scale className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="Selah Slate is NOT a Credit Repair Organization">
            <p>
              Selah Slate is a free educational tool. We do not charge any fee, we do not
              accept any compensation, and we do not act on your behalf with any credit
              bureau, creditor, collector, or government agency. You mail every letter, you
              communicate with every bureau, and you stay in control of every decision.
            </p>
            <p>
              Because of this, the Credit Repair Organizations Act (CROA, 15 U.S.C. § 1679
              et seq.) does not apply to Selah Slate. Nonetheless, in the spirit of full
              transparency we adopt its consumer-protection disclosures below as our own.
            </p>
          </Section>

          {/* FCRA Summary */}
          <Section icon={<Shield className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="A Summary of Your FCRA Rights">
            <p>
              The federal Fair Credit Reporting Act (15 U.S.C. § 1681 et seq.) gives every
              consumer the following baseline rights. Selah Slate is built to help you
              exercise them.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>You have the right to know what is in your file.</strong> You may request disclosure of the information in your consumer file from a credit reporting agency (§ 609).</li>
              <li><strong>You have the right to dispute incomplete or inaccurate information.</strong> The bureau must investigate within 30 days of receiving your dispute unless it deems the dispute frivolous (§ 611).</li>
              <li><strong>You have the right to have inaccurate, incomplete, or unverifiable information corrected or deleted</strong> (§ 611(a)(5)(A)).</li>
              <li><strong>You have the right to a free credit report every 12 months</strong> from AnnualCreditReport.com (§ 612).</li>
              <li><strong>You have the right to place a fraud alert or a free credit freeze</strong> (§§ 605A, 605B).</li>
              <li><strong>You may sue for violations of the FCRA</strong> (§§ 616, 617).</li>
            </ul>
            <p className="mt-3">
              The full "Summary of Your Rights Under the Fair Credit Reporting Act" is
              available from the CFPB at{' '}
              <a
                href="https://files.consumerfinance.gov/f/201504_cfpb_summary_your-rights-under-fcra.pdf"
                target="_blank" rel="noopener noreferrer"
                className="text-[#B76E79] underline"
              >
                files.consumerfinance.gov <ExternalLink className="w-3 h-3 inline-block -mt-0.5" />
              </a>.
            </p>
          </Section>

          {/* Frivolous cap */}
          <Section icon={<AlertTriangle className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="Why we cap disputes at 5 items per round">
            <p>
              FCRA § 611(a)(3)(A) allows a bureau to terminate an investigation if it
              reasonably determines the dispute is frivolous or irrelevant. In practice,
              bureaus often flag batches of <strong>6 or more similar items</strong> as
              frivolous — especially when generated in rapid succession — and send a
              "stall letter" rather than investigating.
            </p>
            <p>
              To protect your dispute from being dismissed outright, Selah Slate caps each
              round at <strong>five items</strong>. After the bureau responds (or after the
              30-day window elapses), you can run the next round. This is the same approach
              used by legitimate consumer-protection attorneys.
            </p>
          </Section>

          {/* CFPB */}
          <Section icon={<Scale className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="How to File a Complaint with the CFPB">
            <p>
              If a credit reporting agency, furnisher, collector, or creditor violates your
              FCRA rights, you can file a free complaint with the Consumer Financial
              Protection Bureau. Selah Slate will walk you through it, but you can also
              file directly at{' '}
              <a
                href="https://www.consumerfinance.gov/complaint/"
                target="_blank" rel="noopener noreferrer"
                className="text-[#B76E79] underline"
              >
                consumerfinance.gov/complaint <ExternalLink className="w-3 h-3 inline-block -mt-0.5" />
              </a>.
            </p>
          </Section>

          {/* Bank-level security */}
          <Section icon={<Lock className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="Bank-Level Security Protocols">
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Encryption in transit</strong> — all traffic is protected with HTTPS / TLS 1.2+.</li>
              <li><strong>Encryption at rest</strong> — the last 4 digits of your SSN are stored encrypted (AES-256).</li>
              <li><strong>Password hashing</strong> — passwords are hashed with bcrypt; they are never stored in plain text and cannot be read by our team.</li>
              <li><strong>Ephemeral PDF processing</strong> — uploaded credit-report text is sent for AI analysis, parsed into structured items, and then <strong>deleted from our servers within minutes</strong>. The original file is never retained.</li>
              <li><strong>Strict HTTP security headers</strong> — HSTS with preload, X-Frame-Options DENY, strict Content-Security-Policy, nosniff, and a restrictive Permissions-Policy, applied to every response.</li>
              <li><strong>No third-party tracking</strong> — no advertising identifiers, no cross-site trackers, no data broker sharing.</li>
              <li><strong>No training on your data</strong> — all AI calls are under a no-training agreement; your report text is never used to train any model.</li>
              <li><strong>Right to deletion</strong> — one click in Settings permanently removes your account and every record tied to it within 30 days.</li>
            </ul>
          </Section>

          {/* No guarantees */}
          <Section icon={<AlertTriangle className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="No Guarantee of Outcome">
            <p>
              No one — not Selah Slate, not a lawyer, not a credit-repair company — can
              guarantee that a specific item will be removed from your credit report or
              that your credit score will improve by a specific amount. Any company that
              makes such a promise is operating outside the law.
            </p>
          </Section>

          {/* Contact */}
          <Section icon={<Shield className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />} title="Contact">
            <p>
              Questions about these disclosures?{' '}
              <a
                href="mailto:privacy@selahandstonelegacy.com"
                className="text-[#B76E79] underline"
              >
                privacy@selahandstonelegacy.com
              </a>
            </p>
          </Section>

        </article>

        <p className="text-xs text-[#36454F]/45 font-['Manrope'] text-center mt-6">
          See also our{' '}
          <Link to="/privacy" className="text-[#B76E79] hover:underline">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms" className="text-[#B76E79] hover:underline">Terms and Conditions</Link>.
        </p>
      </main>

      <AppFooter />
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
          {title}
        </h2>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
